import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { useAuth } from './AuthContext';

const DEFAULT_PERMISSIONS = {
  show_amounts_inventory: false,
  show_amounts_medications: false,
  show_amounts_dispatches: false,
  show_amounts_compras: false,
  access_dashboard: false,
  access_users: false,
  access_settings: false,
};

const PermissionsContext = createContext({ permissions: DEFAULT_PERMISSIONS, role: 'usuario', refresh: () => {} });

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const userKey = user?.id || user?.email || null;
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [role, setRole] = useState('usuario');

  const fetchPermissions = useCallback(async () => {
    if (!userKey) {
      setPermissions(DEFAULT_PERMISSIONS);
      setRole('usuario');
      return;
    }
    try {
      const res = await api.get('/auth/me/permissions');
      setPermissions(res.data?.permissions || DEFAULT_PERMISSIONS);
      setRole(res.data?.role || 'usuario');
    } catch (e) {
      // Si falla, aplicar restrictivo por seguridad
      setPermissions(DEFAULT_PERMISSIONS);
      setRole('usuario');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  useEffect(() => { fetchPermissions(); }, [fetchPermissions]);

  const value = React.useMemo(
    () => ({ permissions, role, refresh: fetchPermissions }),
    [permissions, role, fetchPermissions]
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() {
  return useContext(PermissionsContext);
}

/**
 * Helper: si el usuario puede ver montos de un módulo dado.
 * modules: 'inventory' | 'medications' | 'dispatches' | 'compras'
 */
export function useCanSeeAmounts(module) {
  const { permissions, role } = usePermissions();
  if (role === 'admin') return true;
  return !!permissions[`show_amounts_${module}`];
}

/**
 * Helper: si el usuario tiene acceso a una página.
 * page: 'dashboard' | 'users' | 'settings'
 */
export function useCanAccess(page) {
  const { permissions, role } = usePermissions();
  if (role === 'admin') return true;
  return !!permissions[`access_${page}`];
}
